import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./components/CardUI";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  RadialBarChart,
  RadialBar,
  LabelList,
} from "recharts";
import { Package, RefreshCcw, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import apiService from "../../../core/services/apiService";
import { useDispatch } from "react-redux";
import useSocket from "../../../core/hooks/useSocket";
import StockMovementChart from "./components/StockMovementChart";
import StatModal from "./components/StatModal";
import ReportGenerator from "./components/ReportGenerator";
import {
  computeDeltas,
  computeExpiryBuckets,
  computeLowStock,
  computePrevMonthStats,
  computeSoonToExpire,
  computeTopMovedItems,
  fetchInventoryData,
  getThisMonthTransactions,
  groupItemsByCategory,
} from "./utils/dashboardData";
import PieChartSkeleton from "../../../core/components/skeleton/PieChartSkeleton";
import StockHealthSkeleton from "../../../core/components/skeleton/StockHealthSkeleton";
import BarChartSkeleton from "../../../core/components/skeleton/BarChartSkeleton";
import TopItemsSkeleton from "../../../core/components/skeleton/TopItemsSkeleton";
import StatCard from "./components/StatCard";
import DateSelector from "./components/DateSelector";

export default function InventoryDashboard() {
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStock: 0,
    soonToExpire: 0,
    transactionsThisMonth: 0,
    totalStockIn: 0,
    totalStockOut: 0,
  });

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [categoryData, setCategoryData] = useState([]);

  const [allItems, setAllItems] = useState([]);
  const [monthlyTransactions, setMonthlyTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);

  const [categoryItemsMap, setCategoryItemsMap] = useState({});
  const [topItems, setTopItems] = useState([]);
  const [expiryData, setExpiryData] = useState([]);

  const [activeStat, setActiveStat] = useState(null);
  const [chartData, setChartData] = useState({
    type: null,
    data: null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();

  useSocket({
    // === INVENTORY ITEM EVENTS ===
    inventoryitem_created: (payload) => {
      const newItem = payload.data;
      setAllItems((prev) => {
        const updated = [newItem, ...prev];
        recomputeDashboardData(updated, allTransactions);
        return updated;
      });
    },

    inventoryitem_updated: (payload) => {
      const updatedItem = payload.data;
      setAllItems((prev) => {
        const updated = prev.map((i) =>
          i._id === updatedItem._id ? updatedItem : i
        );
        recomputeDashboardData(updated, allTransactions);
        return updated;
      });
    },

    inventoryitem_deleted: (payload) => {
      const deletedId = payload.data._id;
      setAllItems((prev) => {
        const updated = prev.filter((i) => i._id !== deletedId);
        recomputeDashboardData(updated, allTransactions);
        return updated;
      });
    },

    // === TRANSACTION EVENTS ===
    inventorytransaction_created: (payload) => {
      const newItem = payload.data;
      const newTransaction = {
        ...payload.transaction,
        item: newItem,
      };
      setAllTransactions((prev) => {
        const updated = [newTransaction, ...prev];
        recomputeDashboardData(allItems, updated);
        return updated;
      });
    },

    inventorytransaction_updated: (payload) => {
      const newItem = payload.data;
      const updatedTxn = {
        ...payload.transaction,
        item: newItem,
      };
      setAllTransactions((prev) => {
        const updated = prev.map((t) =>
          t._id === updatedTxn._id ? updatedTxn : t
        );
        recomputeDashboardData(allItems, updated);
        return updated;
      });
    },

    inventorytransaction_deleted: (payload) => {
      const deletedId = payload.data._id;
      setAllTransactions((prev) => {
        const updated = prev.filter((t) => t._id !== deletedId);
        recomputeDashboardData(allItems, updated);
        return updated;
      });
    },
  });

  // === Recompute all dashboard data ===
  const recomputeDashboardData = (items, transactions) => {
    setAllTransactions(transactions);

    const lowStock = computeLowStock(items);
    const soonToExpire = computeSoonToExpire(items);
    const thisMonthTransactions = getThisMonthTransactions(
      transactions,
      selectedYear,
      selectedMonth
    );

    const { map: categoryMap, chartData: categoryChart } =
      groupItemsByCategory(items);
    const topItems = computeTopMovedItems(thisMonthTransactions);
    const expiryData = computeExpiryBuckets(items);

    const { stats: prevStats } = computePrevMonthStats(items, transactions);
    const stockIn = thisMonthTransactions
      .filter((t) => t.type === "Stock in")
      .reduce((sum, t) => sum + (t.quantity || 0), 0);

    const stockOut = thisMonthTransactions
      .filter((t) => t.type === "Stock out")
      .reduce((sum, t) => sum + (t.quantity || 0), 0);

    const deltas = computeDeltas(
      {
        items: items.length,
        stockIn: stockIn,
        stockOut: stockOut,
        transactions: thisMonthTransactions.length,
      },
      {
        items: prevStats.totalItems,
        stockIn: prevStats.totalStockIn,
        stockOut: prevStats.totalStockOut,
        transactions: prevStats.transactions,
      }
    );

    // ✅ set states
    setAllItems(items);
    setMonthlyTransactions(thisMonthTransactions);
    setCategoryData(categoryChart);
    setCategoryItemsMap(categoryMap);
    setExpiryData(expiryData);
    setTopItems(topItems);
    setStats({
      totalItems: items.length,
      lowStock: lowStock.length,
      soonToExpire: soonToExpire.length,
      transactionsThisMonth: thisMonthTransactions.length,
      totalStockIn: stockIn,
      totalStockOut: stockOut,
      deltas,
    });
  };

  useEffect(() => {
    recomputeDashboardData(allItems, allTransactions);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [items, transactions] = await Promise.all([
          apiService.get(dispatch, "inventory"),
          apiService.get(dispatch, "inventoryLogs", {
            year: selectedYear,
          }),
        ]);

        recomputeDashboardData(items, transactions);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dispatch, selectedYear]);

  const handleOpenModal = (type, data) => {
    setActiveStat(type);
    setModalOpen(true);
    setChartData({
      type: type,
      data: data,
    });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setActiveStat(null);
    setChartData({
      type: null,
      data: null,
    });
  };

  const handleChartClick = (type, data) => {
    // type will be one of: "composition", "stockHealth", "expiry"
    setActiveStat("items");
    setChartData({
      type: type,
      data: data,
    });
    setModalOpen(true);
  };

  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#6366f1"];

  return (
    <>
      <div className="p-6 pt-0 space-y-6">
        {/* === HEADER === */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold tracking-tight">
            📊 Inventory Dashboard
          </h1>
          <div className="flex justify-end items-center gap-4">
            <DateSelector
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
            />
            <ReportGenerator
              stats={stats}
              allItems={allItems}
              allTransactions={allTransactions}
            />
          </div>
        </div>

        <div id="dashboard-charts" className="space-y-6">
          {/* === KPI CARDS === */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Package}
              title="Total Items"
              value={stats.totalItems}
              delta={`${stats.deltas?.items || 0}%`}
              trend="up"
              onClick={() => handleOpenModal("items")}
              loading={loading}
            />
            <StatCard
              icon={TrendingUp}
              title="Stock In"
              value={stats.totalStockIn}
              delta={`${stats.deltas?.stockIn || 0}%`}
              trend={stats.deltas?.stockIn >= 0 ? "up" : "down"}
              onClick={() => handleOpenModal("stockIn")}
              loading={loading}
            />
            <StatCard
              icon={TrendingUp}
              title="Stock Out"
              value={stats.totalStockOut}
              delta={`${stats.deltas?.stockOut || 0}%`}
              trend={stats.deltas?.stockOut >= 0 ? "up" : "down"}
              onClick={() => handleOpenModal("stockOut")}
              loading={loading}
            />
            <StatCard
              icon={RefreshCcw}
              title="Transactions"
              value={stats.transactionsThisMonth}
              delta={`${stats.deltas?.transactions || 0}%`}
              trend={stats.deltas?.transactions >= 0 ? "up" : "down"}
              onClick={() => handleOpenModal("transactions")}
              loading={loading}
            />
          </div>

          {/* === CHARTS SECTION === */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* STOCK MOVEMENT TRENDS */}

            <StockMovementChart
              loading={loading}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              allTransactions={allTransactions}
            />

            {/* 🏆 TOP 5 ITEMS */}
            <Card className="shadow-md rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                  🏆 Top 5 Most Moved Items
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Based on total stock ins and outs this month
                </p>
              </CardHeader>

              <CardContent className="pt-0">
                {loading ? (
                  <TopItemsSkeleton />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      layout="vertical"
                      data={topItems}
                      margin={{
                        top: 10,
                        right: 10,
                        left: -60, // 🟢 Pulls chart closer to left edge
                        bottom: 10,
                      }}
                      onClick={(data) => {
                        // ✅ Only trigger when clicking an actual bar
                        if (
                          data &&
                          data.activePayload &&
                          data.activePayload.length > 0
                        ) {
                          handleOpenModal(
                            "topItems",
                            data.activePayload[0].payload
                          );
                        }
                      }}
                      cursor="pointer"
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 12 }}
                        label={{
                          value: "Total Movement",
                          position: "insideBottomRight",
                          offset: -5,
                          fontSize: 12,
                        }}
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={160}
                        tick={({ x, y, payload }) => {
                          const text =
                            payload.value.length > 18
                              ? payload.value.slice(0, 10) + "…"
                              : payload.value;

                          return (
                            <g transform={`translate(${x - 5},${y + 4})`}>
                              <title>{payload.value}</title>{" "}
                              {/* hover tooltip */}
                              <text
                                x={0}
                                y={0}
                                textAnchor="end"
                                fontSize={13}
                                fill="#374151"
                              >
                                {text}
                              </text>
                            </g>
                          );
                        }}
                      />

                      <Tooltip
                        cursor={{ fill: "rgba(0,0,0,0.05)" }}
                        contentStyle={{
                          borderRadius: "10px",
                          borderColor: "#ddd",
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                      <Bar
                        dataKey="stockIn"
                        fill="#10b981"
                        name="Stock In"
                        radius={[0, 6, 6, 0]}
                        barSize={16}
                      />
                      <Bar
                        dataKey="stockOut"
                        fill="#ef4444"
                        name="Stock Out"
                        radius={[0, 6, 6, 0]}
                        barSize={16}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* === HEALTH AND PERFORMANCE SECTION === */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* STOCK HEALTH GAUGE */}
            <Card>
              <CardHeader>
                <CardTitle>❤️ Stock Health Overview</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <StockHealthSkeleton />
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <RadialBarChart
                      innerRadius="60%"
                      outerRadius="100%"
                      data={[
                        {
                          name: "Low Stock",
                          value: stats.lowStock,
                          fill: "#ef4444",
                        },
                        {
                          name: "Healthy",
                          value: stats.totalItems - stats.lowStock,
                          fill: "#10b981",
                        },
                      ]}
                      startAngle={180}
                      endAngle={0}
                    >
                      <RadialBar
                        minAngle={15}
                        background
                        clockWise
                        dataKey="value"
                        cornerRadius={10}
                        cursor="pointer"
                        onClick={(data) =>
                          handleChartClick("stockHealth", data.payload)
                        }
                      >
                        {/* ✅ LabelList must go INSIDE the RadialBar */}
                        <LabelList
                          dataKey="value"
                          position="insideStart" // or "insideEnd" for alternate placement
                          fill="#fff"
                          fontSize={12}
                          fontWeight={600}
                        />
                      </RadialBar>

                      <Legend
                        iconSize={10}
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                      />
                      <Tooltip />
                    </RadialBarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* EXPIRY OVERVIEW */}
            <Card>
              <CardHeader>
                <CardTitle>⏳ Expiry Overview</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <BarChartSkeleton />
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={expiryData}
                      margin={{ top: 20, right: 20, left: 0, bottom: 10 }}
                      barCategoryGap="20%" // Adjusts spacing between bars dynamically
                      barGap={5}
                    >
                      <defs>
                        {/* Gradients for bars */}
                        <linearGradient
                          id="expiredGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#ef4444"
                            stopOpacity={0.9}
                          />
                          <stop
                            offset="100%"
                            stopColor="#dc2626"
                            stopOpacity={0.7}
                          />
                        </linearGradient>
                        <linearGradient
                          id="expiryGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#fbbf24"
                            stopOpacity={0.9}
                          />
                          <stop
                            offset="100%"
                            stopColor="#f59e0b"
                            stopOpacity={0.7}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis
                        dataKey="range"
                        tick={{
                          fill: "#6b7280",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{
                          fill: "#6b7280",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(0,0,0,0.05)" }}
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                      />
                      <Bar
                        dataKey="count"
                        name="Expiring Items"
                        fill="url(#expiryGradient)"
                        radius={[8, 8, 0, 0]}
                        onClick={(data) =>
                          handleChartClick("expiry", data.payload)
                        }
                        cursor="pointer"
                      >
                        {/* Dynamically color based on range */}
                        {expiryData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry.range === "Expired"
                                ? "url(#expiredGradient)"
                                : "url(#expiryGradient)"
                            }
                          />
                        ))}
                        <LabelList
                          dataKey="count"
                          position="top"
                          fill="#374151"
                          fontSize={12}
                          fontWeight={600}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* CATEGORY COMPOSITION */}
            <Card>
              <CardHeader>
                <CardTitle>🧩 Inventory Composition by Category</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <PieChartSkeleton />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                        onClick={(data) =>
                          handleChartClick("composition", data.payload.payload)
                        }
                        cursor="pointer"
                      >
                        {categoryData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <StatModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        type={activeStat}
        chartData={chartData}
        allItems={allItems}
        allTransactions={allTransactions}
        monthlyTransactions={monthlyTransactions}
      />
    </>
  );
}
